<template>
  <div id="map" ref="map">
    <div id="basemap" class="mapboxgl-ctrl-top-right mapboxgl-ctrl">
      <div
          class="mapboxgl-ctrl mapboxgl-ctrl-group basemaps">
        <BaseMapSelect v-on:style-change="setMapStyle($event)"/>
      </div>
    </div>
    <div ref="mapgl" class="mapboxgl-map"></div>
    <MapboxLayer
        name="boundary"
        v-bind:geoJSON="boundary"
        v-bind:before-layer-name=[]
        v-bind:layers="boundaryLayer"
    />
    <ArcRestLayer
        name="tracts_esri"
        v-bind:before-layer-name=beforeLayers
        v-bind:layers="tractsEsriStyle"
        v-bind:interactivity=true
        v-bind:join_column="'geoid10'"
        v-bind:data_columns="['workers16plus']"
        v-bind:requestOptions="tractRequestOptions"
        v-bind:requestGeomOptions="tractGeomRequestOptions"
        v-on:featureClick="cartoLayer_popup($event)"
    />
    <ArcRestLayer
        name="flow"
        v-bind:before-layer-name=beforeLayersFlowSymbol
        v-bind:layers="flowStyle"
        v-bind:join_column="'geoid10'"
        v-bind:data_columns="['flowworkers16plus']"
        v-bind:requestOptions="flowRequestOptions"
        v-bind:requestGeomOptions="flowGeomRequestOptions"
    />
    <div class="map-overlay selectors">
      <AmenitySelect
          class="no-print"
          v-bind:mappedAmenity="mappedAmenity"
          v-bind:amenities="amenities"
          v-bind:mappedCategory="mappedCategory"
          v-bind:categories="categories"
          v-bind:categoryNames="categoryNames"
          v-bind:amenityNames="amenityNames"
          v-bind:counties="counties"
          v-bind:mcd="mcdFilter"
          v-bind:startExpanded="startExpanded"
          v-bind:flow_tract_selected="flow_tract_selected"
          v-on:input="$emit('input', $event)"
          v-on:mcdInput="$emit('mcdInput', $event)"
          v-on:categoryInput="$emit('categoryInput', $event)"
          v-on:countiesInput="$emit('countiesInput', $event)"
          v-on:expandedInput="amenitySelectExpanded = $event"
          v-on:industryInput="industrySelect = $event"
          v-on:occupationInput="occupationSelect = $event"
          v-on:potentialInput="mappedPotentialColumns = $event"
          v-on:flowDirection="$emit('flowDirection', $event)"
          v-on:flowInput="flowSelect = $event"
      />
      <hr v-if="popupFeatureId">
      <DataDive
          v-if="popupFeatureId"
          v-bind:geoid="popupFeatureId"
          v-bind:mappedCategory="mappedCategory === 'Flow' ? 'Industry' : mappedCategory"
          v-bind:naics="naicsCodes"
      />
    </div>
    <div class="map-overlay" id="legend">
      <div v-if="mappedAmenity !== 'Potential'">Workers by {{ amenityNames[mappedAmenity].name }}</div>
      <div v-if="mappedAmenity === 'Potential'">{{ amenityNames[mappedAmenity].name }}</div>
      <div v-if="mappedAmenity !== 'Potential'">(worker/acre)</div>
      <div v-if="mappedAmenity === 'Potential'">(resident/acre)</div>
      <Legend
          :colorLegendData="colorLegendData"
          :format="formatOnLegend"
          :legendMarks="legendMarks"/>
    </div>
    <div class="loader" v-if="loaded">
      <div class="CDB-LoaderIcon CDB-LoaderIcon--big">
        <svg class="CDB-LoaderIcon-spinner" viewBox="0 0 50 50">
          <circle class="CDB-LoaderIcon-path" cx="25" cy="25" r="20" fill="none"/>
        </svg>
      </div>
    </div>

    <div v-show="false">
      <div
          ref="marker"
          class="marker"
          v-bind:style="{'background-color': (flowDirection === 'Inflow' ? amenityNames['Workplace'].buttonColor : amenityNames['Residence'].buttonColor)}"
      >
        <img v-if="flowDirection === 'Inflow'" alt='work' src="../../public/noun_digging_164791.svg"
             style="height: 1em">
        <font-awesome-icon v-else icon="home" style="color: white;"/>
      </div>
      <div ref="popup">
        <div v-if="popupFeatureId && idsInViewport.get(popupFeatureId) && mappedAmenity === 'Potential'">
          <div>Nonworking Residents: {{ format(idsInViewport.get(popupFeatureId).workers16plus) }}</div>
          <div>Residents/acre: {{
              formatOnLegend(idsInViewport.get(popupFeatureId).workers16plus / idsInViewport.get(popupFeatureId).totarea_ac)
            }}
          </div>
        </div>
        <div v-if="popupFeatureId && idsInViewport.get(popupFeatureId) && mappedAmenity !== 'Potential'">
          <div>Workers by {{ mappedAmenity }}: {{ format(idsInViewport.get(popupFeatureId).workers16plus) }}</div>
          <div>Workers/acre: {{
              formatOnLegend(idsInViewport.get(popupFeatureId).workers16plus / idsInViewport.get(popupFeatureId).totarea_ac)
            }}
          </div>
        </div>
      </div>
      <div ref="flowMarkerPopup">
        <DataOutflow
            v-bind:flowDirection="flowDirection"
            v-bind:flowSelect="flowSelect"
            v-bind:flow_tract_selected="flow_tract_selected"
            v-bind:flowNames="flowNames"
        />
      </div>
    </div>
  </div>
</template>

<script>
import mapboxgl from 'mapbox-gl'
import MapboxLayer from "./MapboxLayer";
import cartocolor from 'cartocolor'
import debounce from 'lodash.debounce'
import {naturalBreaks} from './NaturalBreaks'
import BaseMapSelect from "./BaseMapSelect";
import AmenitySelect from "./AmenitySelect";
import Legend from "./Legend";
import * as d3 from 'd3'
import * as turf from 'turf'
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder'
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css'
import DataDive from "@/components/DataDive";
import DataOutflow from "@/components/DataOutflow";
import ArcRestLayer from "@/components/ArcRestLayer.vue";
import {queryFeatures} from '@esri/arcgis-rest-feature-service'

export default {
  name: "mainmap",
  components: {ArcRestLayer, DataOutflow, DataDive, MapboxLayer, BaseMapSelect, AmenitySelect, Legend},
  props: ['mcdFilter', 'counties', 'mappedAmenity', 'mappedCategory', 'flowDirection'],
  data: function () {
    return {
      sqlInViewport: "",
      idsInViewport: new Map(),
      overrideClassification: null,
      amenityNames: {
        "Workplace": {
          "name": "Workplace",
          "color": ['#eff3ff', '#bdd7e7', '#6baed6', '#3182bd', '#08519c'],
          'defaultFlow': 'Inflow',
          "buttonColor": '#08519c',
        },
        "Residence": {
          "name": 'Residence',
          "color": ['#fee5d9', '#fcae91', '#fb6a4a', '#de2d26', '#a50f15'],
          'defaultFlow': 'Outflow',
          "buttonColor": '#a50f15',
        },
        "Potential": {
          "name": 'Nonworking Residents',
          "color": ['#ffffcc', '#c2e699', '#78c679', '#31a354', '#006837'],
          'defaultFlow': 'Outflow',
          "buttonColor": cartocolor['SunsetDark'][5][4],
        }
      },
      mappedPotentialColumns: [],
      amenities: ["Residence", "Workplace", "Potential"],
      categories: ['Industry', 'Occupation', 'Flow'],
      categoryNames: {
        "Industry": {
          "name": "Industry",
        },
        "Occupation": {
          "name": 'Occupation',
        },
        "Flow": {
          "name": 'Commuter Flow',
        }
      },
      flow_tract_selected: 26125162400, //default so that the layer loads in the background
      flowNames: {
        "Outflow": {
          "name": "work",
        },
        "Inflow": {
          "name": "live",
        },
      },
      maxOnMap: Infinity,
      regionBoundarys: undefined,
      startExpanded: false,
      amenitySelectExpanded: this.startExpanded,
      industrySelect: [],
      occupationSelect: [],
      flowSelect: [],
      zoomControl: new mapboxgl.NavigationControl({visualizePitch: true}),
      loaded: false,
      currentPopup: new mapboxgl.Popup({maxWidth: 'none', closeOnClick: false, className: 'tract-marker-popup'}),
      popupFeatureId: null,
      flowMarker: {},
      flowMarkerPopup: new mapboxgl.Popup({anchor: 'top', offset: [0, 15], className: 'flow-marker-popup'}),
    }
  },
  computed: {
    map: function () {
      mapboxgl.accessToken = 'pk.eyJ1Ijoic2VtY29nIiwiYSI6ImlvNEFUcmMifQ.VmDLZZG7l6syscFzkBs5eg'
      return new mapboxgl.Map({
        container: this.$refs.mapgl,
        style: 'mapbox://styles/semcog/cilnv2pcp005x9kly0if8ttqh',
        center: [-83.2437186609522, 42.454430721108764],
        zoom: 8.3,
        minZoom: 7,
        maxZoom: 18,
        maxBounds: [-87, 40, -79, 44],
        scrollZoom: true,
      }).addControl(new MapboxGeocoder({
        accessToken: mapboxgl.accessToken,
        bbox: [-85.11, 41.55, -81.46, 43.32],
        collapsed: true,
        marker: true,
        flyTo: true,
        mapboxgl: mapboxgl,
      }), 'top-right').addControl(this.zoomControl, 'top-right');
    },
    beforeLayers: function () {
      return ['flow_circle_0', 'mi-county-labelpoints', 'waterway-label', 'place-village', 'mcd-pop-label-2uiaf9', 'local-dx1f7q']
    },
    beforeLayersFlowSymbol: function () {
      return ['flow_symbol_1', 'mi-county-labelpoints', 'waterway-label', 'place-village', 'mcd-pop-label-2uiaf9', 'local-dx1f7q']
    },
    tractIds: function () {
      if (!this.regionBoundarys) {
        return this.regionBoundarys
      }
      if (Array.isArray(this.mcdFilter)) {
        let s = new Set()
        this.mcdFilter.forEach(mcd => {
          this.regionBoundarys[mcd].properties.tract_ids.forEach(id => s.add(id))
        })
        return [...s]
      }

      let bound = this.regionBoundarys[this.mcdFilter]

      if (bound) {
        return bound.properties && bound.properties.tract_ids
      }
      return bound
    },
    boundary: function () {
      if (!this.regionBoundarys) {
        return this.regionBoundarys
      }
      if (Array.isArray(this.mcdFilter)) {
        return turf.union(turf.featureCollection(this.mcdFilter.map(m =>
            this.regionBoundarys[m] || this.regionBoundarys[0])))
      }
      return turf.rewind(this.regionBoundarys[this.mcdFilter || 0])
    },
    boundaryLayer: function () {
      return [
        {
          type: 'line',
          paint: {
            'line-width': 4,
            'line-opacity': 0.9,
            'line-color': 'black',
            'line-blur': 3
          }
        }
      ]
    },
    tractsEsriStyle: function () {
      let line_filter = ['==', 'geoid10', 0]
      if (this.mappedCategory === 'Flow') {
        line_filter = ['==', 'geoid10', this.flow_tract_selected]
      }
      let filter = ['>', 'workers16plus', 0]
      let color = '#ffffff'
      if (this.breakPoints.length) {
        color = ['step', ['/', ['get', 'workers16plus'], ['get', 'totarea_ac']]]

        for (let i = 0; i < this.breakPoints.length; i += 1) {
          color.push(this.colorPalette[i], this.breakPoints[i])
        }
        color.push(this.colorPalette[this.breakPoints.length])
      }
      return [
        {
          type: 'fill',
          filter: filter,
          paint: {
            'fill-color': color,
            'fill-opacity': 0.8,
          },
        },
        {
          type: 'fill',
          filter: line_filter,
          paint: {
            'fill-color': 'red',
            'fill-opacity': .3,
          },
        }, {
          type: 'line',
          filter: line_filter,
          paint: {
            'line-opacity': 0.8,
            'line-color': 'red',
            'line-width': 4,
          },
        },
      ]
    },
    flowStyle: function () {
      let filter = ['>', ['get', 'flowworkers16plus'], 0]
      let radius = 0;

      radius = ['step', ['get', 'flowworkers16plus'],
        2, 10,
        8, 50,
        12, 100,
        20, 500,
        25
      ]

      let text_size = [
        'interpolate',
        ['linear'],
        ['zoom'],
        8,
        12,
        9,
        14,
        11,
        16,
        22,
        20]

      let color = '#ffa03f'

      if (this.flowDirection === 'Inflow') {
        color = '#a50f15'
      } else {
        color = '#08519c'
      }

      return [
        {
          type: 'circle',
          filter: filter,
          layout: {
            visibility: (this.mappedCategory === 'Flow') ? 'visible' : 'none',
          },
          paint: {
            'circle-color': color,
            'circle-opacity': 0.6,
            'circle-radius': radius,
            'circle-stroke-width': 3,
            'circle-stroke-color': color,
          },
        },
        {
          type: 'symbol',
          filter: filter,
          layout: {
            'text-field': ['get', 'flowworkers16plus'],
            'text-size': text_size,
            visibility: (this.mappedCategory === 'Flow') ? 'visible' : 'none',
          },
          paint: {
            'text-opacity': 0.7,
            'text-halo-width': 1,
            'text-halo-color': 'white',
          },
        },
      ]
    },
    rawBreakPoints: function () {
      let raw_values
      let values
      raw_values = Array.from(this.idsInViewport.values())
          .filter(f => (
              f &&
              f.workers16plus &&
              f.totarea_ac
          ))
      values = raw_values
          .map(f => (f.workers16plus / f.totarea_ac))
      let maxvalue = Math.max(0, ...values)
      let minvalue = Math.min(Infinity, ...values)
      let count = values.length || 0
      if (count === 0 || maxvalue === 0) {
        return []
      }

      let raw = []
      let classification = this.overrideClassification;
      if (classification === 'divergingRatio') {
        raw = [
          1 - ((1 - minvalue) / 2),
          1,
          ((maxvalue - 1) / 3) + 1,
        ]
      } else if (classification === 'AreaNaturalBreaks') {
        raw = naturalBreaks(values, 4, raw_values.map(f => f.totarea_ac))
      } else if (classification === 'AreaLogNaturalBreaks') {
        raw = naturalBreaks(values.map(Math.log1p), 4, raw_values.map(f => f.totarea_ac)).map(Math.expm1)
      } else if (classification === 'NaturalBreaks') {
        raw = naturalBreaks(values, 4)
      } else if (classification === 'LogNaturalBreaks') {
        raw = naturalBreaks(values.map(Math.log1p), 4).map(Math.expm1)
      } else if (classification === 'AreaViewportPercentile') {
        let sorted = raw_values.sort((a, b) =>
            (a.workers16plus) -
            (b.workers16plus)
        )
        let cumsumAcres = sorted.map((sum => value => sum += value.totarea_ac)(0))
        let totalAcres = cumsumAcres[cumsumAcres.length - 1]
        let percentiles = [0.05, 0.30, 0.70, 0.95]
        raw = percentiles.map(v => {
          let i = cumsumAcres.findIndex(s => s >= (v * totalAcres)) // TODO O(n^2)
          return sorted[i].workers16plus
        })
      } else {
        // defeat to viewportPercentile if custom map

        let sorted = values.sort((a, b) => a - b)
        let percentiles = [0.05, 0.30, 0.70, 0.95]
        raw = percentiles.map(v => sorted[Math.floor(v * count)])
      }
      return raw
    },
    breakPoints: function () {
      let out = []
      let max = -Infinity
      this.rawBreakPoints.forEach(b => {
        if (b > max) {
          out.push(b)
          max = b
        }
      })
      return out
    },
    colorPalette: function () {
      let palette = this.amenityNames[this.mappedAmenity].color;

      let override = palette;

      if (Array.isArray(palette)) {
        override = palette;
      }

      return override || []
    },
    legendMarks: function () {
      if (!this.colorLegendData) {
        return []
      }
      let d = [0].concat(this.colorLegendData.map(legend => legend.key[1]).filter(isFinite)).concat([this.maxOnMap])
      return Array.from(new Set(d))
    },
    colorLegendData: function () {
      let out = []
      for (let i = 0; i <= this.breakPoints.length; i += 1) {
        out.push(
            {
              key: [
                this.breakPoints[i - 1] || -Infinity,
                this.breakPoints[i] || Infinity,
              ],
              value: this.colorPalette[i],
            }
        )
      }
      return out
    },
    naicsCodes: function () {
      let ids = this.industrySelect;
      if (this.mappedCategory === 'Occupation') {
        ids = this.occupationSelect;
      }
      ids = ids.map(i => i.statevalue)
      if (this.mappedCategory === 'Flow') {
        ids = [].concat(this.flowSelect.map(i => i.industry_codes))
      }
      return ids
    },
    tractGeomRequestOptions: function () {
      let tract_codes = '1=1';
      if (Array.isArray(this.tractIds) && this.tractIds.length !== 0) {
        tract_codes = `geoid10 IN (${this.tractIds.join(',')})`;
      }
      return {
        url: "https://services1.arcgis.com/xUx8EjNc6egUPYWh/arcgis/rest/services/worker_tracts_with_semmcd/FeatureServer/0",
        where: tract_codes,
        f: "geojson",
        returnGeometry: true,
        outFields: ['geoid10', 'semmcd', 'totarea_ac']
      }
    },
    tractRequestOptions: function () {
      let category = this.mappedCategory
      if (this.mappedCategory === 'Flow') {
        category = 'Industry'
      }
      let codes = `naics_soc IN (${this.naicsCodes.join(',')})`;
      if (this.naicsCodes.length === 0) {
        codes = `1=1`;
      }
      let tract_codes = '1=1';
      if (Array.isArray(this.tractIds) && this.tractIds.length !== 0) {
        tract_codes = `geoid10 IN (${this.tractIds.join(',')})`;
      }
      let column = 'workers16plus'
      // when updating this table and all other tables (not feature layers) in this project, make sure to update the definition to allow for 2000 return records, the default is 1000
      let url = "https://services1.arcgis.com/xUx8EjNc6egUPYWh/arcgis/rest/services/workers_by_industryandoccupation_by_tract/FeatureServer/0"
      let where = `indicator = '${category}' and dset = '${this.mappedAmenity}' and ${tract_codes} and ${codes} and naics_soc <> 99 and naics_soc <> 990000`;

      if (this.mappedAmenity === 'Potential') {
        url = 'https://services1.arcgis.com/xUx8EjNc6egUPYWh/arcgis/rest/services/worker_potential_2015_2019/FeatureServer/0'
        column = this.mappedPotentialColumns.map(c => c.statevalue).join('+')
        if (column.length === 0) {
          column = 'total_potential_workers'
        }
        where = tract_codes
      }

      return {
        url: url,
        where: where,
        groupByFieldsForStatistics: 'geoid10',
        outStatistics: [
          {
            onStatisticField: column,
            statisticType: 'sum',
            outStatisticFieldName: 'workers16plus',
          }],
        f: "json",
        outFields: ['geoid10', 'workers16plus']
      }
    },
    tractMaxRequestOptions: function () {
      let category = this.mappedCategory
      if (this.mappedCategory === 'Flow') {
        category = 'Industry'
      }
      let codes = `naics_soc IN (${this.naicsCodes.join(',')})`;
      if (this.naicsCodes.length === 0) {
        codes = `1=1`;
      }
      let tract_codes = '1=1';
      if (Array.isArray(this.tractIds) && this.tractIds.length !== 0) {
        tract_codes = `geoid10 IN (${this.tractIds.join(',')})`;
      }
      let column = 'workers16plus'
      let url = "https://services1.arcgis.com/xUx8EjNc6egUPYWh/arcgis/rest/services/workers_by_industryandoccupation_by_tract/FeatureServer/0"
      let where = `indicator = '${category}' and dset = '${this.mappedAmenity}' and ${tract_codes} and ${codes} and naics_soc <> 99 and naics_soc <> 990000`;

      if (this.mappedAmenity === 'Potential') {
        url = 'https://services1.arcgis.com/xUx8EjNc6egUPYWh/arcgis/rest/services/worker_potential_2015_2019/FeatureServer/0'
        column = this.mappedPotentialColumns.map(c => c.statevalue).join('+')
        if (column.length === 0) {
          column = 'total_potential_workers'
        }
        where = tract_codes
      }
      return {
        url: url,
        where: where,
        groupByFieldsForStatistics: 'geoid10',
        orderByFields: `'maxWorkers16plus' desc`,// this is dumb syntax
        resultRecordCount: 1,
        outStatistics: [
          {
            onStatisticField: `(${column})/totarea_ac`,
            statisticType: 'sum',
            outStatisticFieldName: 'maxWorkers16plus',
          }
        ],
        f: "json",
      }
    },
    flowGeomRequestOptions: function () {
      return {
        url: "https://services1.arcgis.com/xUx8EjNc6egUPYWh/arcgis/rest/services/worker_tract_points/FeatureServer/0",
        where: '1=1',
        f: "geojson",
        returnGeometry: true,
        outFields: ['geoid10', 'totarea_ac']
      }
    },
    flowRequestOptions: function () {
      let join = 'wrktract'
      let filter = 'res_tract'

      if (this.flowDirection === "Inflow") {
        join = 'res_tract'
        filter = 'wrktract'
      }
      let codes = `flow_naics_code IN (${this.flowSelect.map(i => i.statevalue).join(',')})`;
      if (this.flowSelect.length === 0) {
        codes = `flow_naics_code = 99`;
      }
      let flow_tract = `${filter} = ${this.flow_tract_selected}`
      let where = flow_tract + ' and ' + codes

      return {
        url: 'https://services1.arcgis.com/xUx8EjNc6egUPYWh/arcgis/rest/services/all_flows_to_region_tracts_by_industry_2016/FeatureServer/0',
        where: where,
        f: "json",
        outFields: [`(${join}) as geoid10`, 'flowworkers16plus']
      }
    },
  },
  methods: {
    setStyle: debounce(function () {
      if (this.map.getLayer('tracts_esri')) {
        this.$nextTick(() => {
          this.loaded = true
        })
      }

      if (!this.map.getLayer('tracts_esri_fill_0')) {
        return
      }

      let features = this.map.queryRenderedFeatures({layers: ['tracts_esri_fill_0']});
      // queryRenderedFeatures can return duplicates so filter them out
      features = Object.freeze(new Map(features.map(f => [f.properties.geoid10, f.properties])));
      if (
          features.size > 0 && (
              this.sqlInViewport !== JSON.stringify(this.tractRequestOptions) ||
              features.size !== this.idsInViewport.size ||
              Array.from(features.keys()).some(id => !this.idsInViewport.has(id))
          )
      ) {
        this.idsInViewport = features
        this.sqlInViewport = JSON.stringify(this.tractRequestOptions)
      }
    }, 100, {leading: true, maxWait: 600}),
    setMapStyle: function (e) {
      this.map.setStyle(e)
    },
    formatOnLegend: function (x) {
      if (isFinite(x)) {
        return d3.format('.2f')(x)
      }
      return 'Max'
    },
    format: d3.format(',d'),
    fitToBoundary: function () {
      let bbox = turf.bbox(this.boundary)
      this.map.fitBounds(
          [[bbox[0], bbox[1]], [bbox[2], bbox[3]]],
      )
    },
    getMarkerGeoid: function () {
      let features = this.map.queryRenderedFeatures(this.map.project(this.flowMarker.getLngLat()), {layers: ['tracts_esri_fill_0']})

      if (features.length >= 1) {
        this.flow_tract_selected = features[0].properties.geoid10
      }
    },
    moveFlowMarker: function (useBoundary) {
      if (this.mappedCategory === 'Flow') {
        if (useBoundary) {
          this.flowMarker.setLngLat(turf.center(this.boundary).geometry.coordinates)
        } else {
          this.flowMarker.setLngLat(this.map.getCenter())
        }
        this.flowMarker.setPopup(this.flowMarkerPopup).addTo(this.map)
        this.flowMarker.togglePopup()

        if (this.map.getLayer('tracts_esri_fill_0')) {
          this.getMarkerGeoid()
        }
      } else {
        this.flow_tract_selected = 26125162400
        this.flowMarker.remove()
      }
    },
    cartoLayer_popup: function (featureEvent) {
      const feature = featureEvent.features[0];
      if (!feature) {
        return;
      }
      this.currentPopup.setLngLat([featureEvent.lngLat.lng, featureEvent.lngLat.lat]).addTo(this.map);
      this.popupFeatureId = feature.properties.geoid10;
    },
  },
  mounted: function () {
    //regionBoundaries.geojson made from this query
    // SELECT ARRAY_AGG(t.geoid10) as tract_ids,  c.the_geom, c.semmcd as city_id, c.name FROM
    // semcog.mcd_tract10_majorparts t
    // JOIN
    // communityboundary c
    // ON
    // t.semmcd = c.semmcd
    // group by c.the_geom, c.semmcd, c.name
    fetch('geojson/regionBoundaries.geojson')
        .then(res => res.json())
        .then(res => {
          let regionBoundarys = {};
          regionBoundarys[0] = {
            type: 'FeatureCollection',
            features: [],
          }
          res.features.forEach(f => {
            regionBoundarys[f.properties.city_id] = f;
          });
          this.regionBoundarys = regionBoundarys;
        });
    this.flowMarker = new mapboxgl.Marker({element: this.$refs.marker, draggable: true});
    this.currentPopup.setDOMContent(this.$refs.popup)
        .on('close', () => this.popupFeatureId = null);
    this.flowMarkerPopup.setDOMContent(this.$refs.flowMarkerPopup);
    this.map.once('idle', () => {
      this.setStyle();
      this.moveFlowMarker(false);
      this.flowMarker.on('drag', debounce(this.getMarkerGeoid, 150, {leading: true, maxWait: 600}))
      this.map.on('sourcedata', (e) => {
        if (e.sourceId === 'tracts_esri') {
          this.setStyle();
        }
      });
    });
  },
  watch: {
    boundary: function () {
      if (this.boundary && this.boundary.geometry) {
        this.fitToBoundary();
        this.moveFlowMarker(true)
      }
    },
    mappedCategory: function () {
      this.moveFlowMarker(false)
    },
    tractRequestOptions: {
      immediate: true,
      handler: function () {
        this.maxOnMap = Infinity
        // cartoSqlFetch(`SELECT MAX(z.workers16plus/z.totarea_ac) FROM (${this.tractSQL.replace(/\s+/g, ' ')}) z`)
        //     .then(res => this.maxOnMap = res.rows[0].max)
        queryFeatures(this.tractMaxRequestOptions).then(res => {
          this.maxOnMap = res.features[0].attributes['maxWorkers16plus']
        })
      }
    },
  }
}
</script>

<style scoped>
#map {
  grid-row: 2;
  position: relative;
}

/deep/ .mapboxgl-map {
  width: 100%;
  height: 100%;
}

#legend {
  height: fit-content;
  margin-bottom: 24px;
  margin-right: 10px;
  padding: 8px;
  text-align: center;
  text-align: -webkit-center;
  text-align: -moz-center;
  color-adjust: exact;
}

#legend table {
  border-collapse: collapse;
  align-self: center;
}

/deep/ #legend th select {
  border-radius: unset;
}

/deep/ #legend th select {
  border-color: rgba(243, 243, 243, 0);
}

/deep/ #legend th select:focus {
  outline: 0;
  border: unset;
}

/deep/ #legend th select {
  font-size: 1rem;
  font-weight: bold;
}

#legend td, #legend th {
  border: 1px solid #999;
  padding: 0.2rem;
}

.text_halo {
  text-shadow: -0.05em 0 black, 0 0.05em black,
  0.05em 0 black, 0 -0.05em black;
  color: #ffffff;
}

#legend td {
  text-align: center;
  font-size: small;
  opacity: .3;
}

#legend tr td.activate {
  opacity: .95;
}

.demographic-legend span {
  padding: 2px;
}

.marker {
  opacity: .9;
  font-size: 2em;
  background-color: white;
  padding: 5px;
  background-size: cover;
  border-radius: 20%;
  cursor: pointer;
}

/deep/ .flow-marker-popup {
  opacity: .8;
}

/deep/ .tract-marker-popup {
  opacity: .8;
}

div.selectors {
  top: 0;
  bottom: unset;
  height: fit-content;
  margin-top: 20px;
  right: auto;
  margin-left: 20px;
  padding: 8px;
}

.map-overlay {
  position: absolute;
  bottom: 0;
  right: 0;
  background: rgba(255, 255, 255, 1);
  font-family: Arial, sans-serif;
  border-radius: 3px;
}

/deep/ .popup-header {
  background-color: #011f4a;
  color: white;
  padding: 10px;
}

/deep/ .content th {
  border-bottom: 1px solid #ddd;
}

/deep/ .content td {
  border-bottom: 1px solid #ddd;
}

/deep/ .field-header {
  font-weight: bolder;
}

#basemap {
  pointer-events: none;
  position: absolute;
  margin-top: 145px;
}

.loader {
  position: absolute;
  background: rgba(46, 60, 67, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 2;
  opacity: 1;
  transition: opacity 300ms;
  pointer-events: none;
}

.CDB-LoaderIcon--big {
  width: 40px;
  height: 40px;
}

.CDB-LoaderIcon-spinner {
  width: 40px;
  height: 40px;
  animation: rotate 2s linear infinite;
}

.CDB-LoaderIcon-path {
  stroke: rgba(255, 255, 255, .88);
  stroke-linecap: round;
  animation: dash 1.5s ease-in-out infinite;
  stroke-width: 4px
}

@keyframes dash {
  0% {
    stroke-dasharray: 1, 150;
    stroke-dashoffset: 0
  }

  50% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -35
  }

  100% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -124
  }
}

@keyframes rotate {
  100% {
    transform: rotate(360deg)
  }
}

@media print {
  /deep/ .mapboxgl-ctrl-geocoder, .mapboxgl-ctrl-group {
    display: none;
  }

  /deep/ select {
    appearance: none;
    border: none;
    /* needed for Firefox: */
    overflow: hidden;
  }

  /deep/ .mapboxgl-control-container {
    display: none;
  }
}

@media print {
  .no-print {
    display: none !important;
  }
}

@media screen and (max-width: 1000px) {
  #map {
    display: grid;
    grid-template-columns: auto max-content;
    grid-template-rows: auto max-content;
  }

  .map-overlay {
    margin-right: unset;
    position: unset;
  }

  div.selectors {
    margin-left: unset;
    margin-top: unset;
    grid-row: 1;
    grid-column: 2;
  }

  #basemap {
    position: relative;
    grid-area: 1 / 1;
  }

  .mapboxgl-map {
    grid-row-start: 1;
    grid-row-end: 3;
    grid-column: 1;
  }

  #legend {
    grid-row: 2;
    grid-column: 2;
    margin: 0;
  }
}

@media screen and (max-width: 800px) {
  #map {
    display: grid;
    grid-template-rows: auto max-content max-content;
    grid-template-columns: auto;
  }

  div.selectors {
    grid-row: 1;
    grid-column: 1;
  }

  .mapboxgl-map {
    grid-row: 2;
    height: 75vh;
  }

  #basemap {
    position: relative;
    grid-area: 2 / 1;
    margin-top: 155px;
  }

  #legend {
    grid-row: 3;
    grid-column: 1;
  }
}
</style>
